// Copyright (c) 2017+,COMUNICARE SOLUTIONS SA
// All rights reserved.
//
// Redistribution and use in source and binary forms, with or without modification,
// are NOT permitted.

import { Group, ImageUrls, INPUT_TYPE, IQuestionnaire, Questionnaire } from "../models/questionnaire";
import { ACTION_STATUS_ENTITY, Coding, Identifier, IEntity, Reference, STATUS_ENTITY, SPECIFIC_USE } from "../models/sharedInterfaces";
import { IAccount } from "./account-helper";
import { FHIRHelper } from "./FHIR-helper";
import { Tools } from "./tools-helper";
import { IEnableWhen } from "./enableWhen-helper";

export class Answer {
  valueCoding: Coding;
}
export class QRQuestion {
  linkId: string;
  text: string;
  imageUrls?: ImageUrls[];
  enableWhen?: IEnableWhen[];
  answer: Answer[];
  inputType?: INPUT_TYPE;
}
export class QRGroup {
  linkId: string;
  title: string;
  text: string;
  question?: QRQuestion[];
  group?: QRGroup[];
}

/**
 * Class for Questionnaire Response
 */
export class QuestionnaireResponse extends IEntity {
  resourceType: string;
  identifier: Identifier;
  questionnaire: Reference;
  status: string;
  subject: Reference;
  author: Reference;
  authored: string;
  group: QRGroup;
  source: Reference;
  specificUse?: SPECIFIC_USE;
  successThreshold?: number;
  knowledgeId?: string;
  mediaId?: string;
  knowledgePublicationDate?: string;
  onlyOnline?: boolean;
  questionnairePublicationDate?: string;

  /**
   * constructor
   */
  constructor() {
    super();
    this.resourceType = FHIRHelper.RES_QUESTIONNAIRERESPONSE;
  }

  /**
   * Returns "true" is this questionnaire has sub-groups with questions
   * Returns "false" if only questions under main group
   */
  public static isMultiGroup(qresponse: QuestionnaireResponse): boolean {
    try {
      return qresponse.group.group && qresponse.group.group.length > 0;
    } catch (err) {
      return false;
    }
  }

  /**
   * Build a questionnaire response from a questionnaire template
   */
  public fromTemplate(template: IQuestionnaire, account: IAccount): void {
    this._id = Tools.genValidId();
    const id = Tools.genValidId();
    this.actionStatus = ACTION_STATUS_ENTITY.CREATED;
    this.entityStatus = [STATUS_ENTITY.ACTIVE];
    this.identifier = {
      label: id,
      system: template.identifier[0].system,
      use: "",
      value: template.identifier[0].value + "/" + id,
    };
    this.questionnaire = {
      display: template.subjectType,
      reference: Questionnaire.getIdentifier4Response(template),
    };
    this.status = FHIRHelper.QRESPONSE_STATUS_INPROGRESS;

    this.subject = FHIRHelper.account2Reference(account);
    this.source = FHIRHelper.account2Reference(account);
    // author will be filled server-side
    this.author = FHIRHelper.account2Reference(account);
    this.authored = Tools.nowISO8601();

    this.group = new QRGroup();
    this.group.linkId = template.group.linkId;
    this.group.title = template.group.title;
    this.group.text = template.group.text;

    if (template.specificUse === SPECIFIC_USE.QUIZ || template.specificUse === SPECIFIC_USE.CONSENT) {
      this.specificUse = template.specificUse;
      this.successThreshold = template.successThreshold;
      this.knowledgeId = template.knowledgeId;
      this.mediaId = template.mediaId;
    }

    // main GROUP can contain sub-groups OR questions (not both)
    if (template.group.group && template.group.group.length > 0) {
      this.group.group = new Array<QRGroup>();
      for (const subGroup of template.group.group) {
        const qrGroup = new QRGroup();
        qrGroup.linkId = subGroup.linkId;
        qrGroup.title = subGroup.title;
        qrGroup.text = subGroup.text;
        this.loadSubGroupQuestions(subGroup, qrGroup);
        this.group.group.push(qrGroup);
      }
    } else if (template.group.question && template.group.question.length > 0) {
      // Simple questionnaire: no sub-groups, questions are directly in main group
      this.loadSubGroupQuestions(template.group, this.group);
    }
  }

  /**
   *
   */
  private loadSubGroupQuestions(questionnaireGroup: Group, responseGroup: QRGroup) {
    if (questionnaireGroup.question && questionnaireGroup.question.length > 0) {
      responseGroup.question = new Array<QRQuestion>();
      for (const question of questionnaireGroup.question) {
        const q = new QRQuestion();
        q.linkId = question.linkId;
        q.text = question.text;
        q.imageUrls = question.imageUrls;
        q.answer = [];
        responseGroup.question.push(q);
      }
    }
  }
}
